import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { forwardRef, Fragment, type ReactNode } from 'react';
import { classNames } from '~/utils/classNames';

export type ActionMenuType = 'neutral' | 'error';

export interface ActionMenuItemData {
  id: string;
  type?: ActionMenuType;
  icon?: ReactNode;
  label: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  selected?: boolean;
  section?: string;
  submenu?: ActionMenuItemData[];
  onAction?: (key: string) => void;
}

const ActionMenuItem = forwardRef<HTMLDivElement, ActionMenuItemData>(
  ({ id, icon, label, shortcut, disabled, selected, submenu, onAction, type = 'neutral' }, ref) => {
    const labelContent = (
      <div className="flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
    );

    const actionContent = (
      <div className="flex items-center gap-1 text-sm font-normal">
        {shortcut && <span className="bolt-font-monospace text-bolt-elements-icon-secondary">{shortcut}</span>}
        {submenu && <div className="i-ph:caret-right text-bolt-ds-textSecondary" />}
      </div>
    );

    const itemProps = {
      className: classNames(
        'flex items-center justify-between gap-8 p-2 text-sm rounded-md cursor-pointer',
        'bg-transparent border-transparent outline-none',
        'ring-none focus-visible:ring-2',
        {
          'font-semibold': selected,
          'op-50 cursor-not-allowed': disabled,
          'text-bolt-ds-textPrimary hover:bg-bolt-ds-surfaceHighlight focus-visible:ring-bolt-ds-brandHighlight':
            type === 'neutral' && !disabled,
          'text-bolt-ds-textTertiary': type === 'neutral' && disabled,
          'text-bolt-ds-danger hover:bg-bolt-ds-danger/20 focus-visible:ring-bolt-ds-dangerHighlight':
            type === 'error' && !disabled,
          'text-bolt-ds-danger/75': type === 'error' && disabled,
        },
      ),
      disabled,
      onSelect: () => onAction?.(id),
    };

    return submenu ? (
      <RadixDropdownMenu.Sub key={id}>
        <RadixDropdownMenu.SubTrigger ref={ref} {...itemProps}>
          {labelContent}
          {actionContent}
        </RadixDropdownMenu.SubTrigger>
        <RadixDropdownMenu.Portal>
          <RadixDropdownMenu.SubContent className="z-max rounded-lg p-1 border border-bolt-ds-surface-highlight bg-bolt-ds-surfaceOne text-bolt-ds-textPrimary shadow-lg">
            {submenu.map((subItem) => (
              <ActionMenuItem key={subItem.id} {...subItem} />
            ))}
          </RadixDropdownMenu.SubContent>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Sub>
    ) : (
      <RadixDropdownMenu.Item key={id} ref={ref} {...itemProps}>
        {labelContent}
        {actionContent}
      </RadixDropdownMenu.Item>
    );
  },
);

interface ActionMenuPopupProps {
  items: ActionMenuItemData[];
  contentClassName?: string;
}

const ActionMenuPopup = forwardRef<HTMLDivElement, ActionMenuPopupProps>(({ items, contentClassName }, ref) => {
  const groupedItems = items.reduce<Record<string, ActionMenuItemData[]>>((acc, item) => {
    const section = item.section || 'default';
    acc[section] = [...(acc[section] || []), item];

    return acc;
  }, {});

  return (
    <RadixDropdownMenu.Portal>
      <RadixDropdownMenu.Content
        ref={ref}
        sideOffset={4}
        className={classNames(
          'z-max rounded-lg p-1 border border-bolt-ds-surface-highlight bg-bolt-ds-surfaceOne text-bolt-ds-textPrimary shadow-lg',
          contentClassName,
        )}
      >
        {Object.entries(groupedItems).map(([section, items]) => (
          <Fragment key={section}>
            {section !== 'default' && <div className="my-4 px-6 text-sm font-semibold">{section}</div>}
            {items.map((item) => (
              <ActionMenuItem key={item.id} {...item} />
            ))}
          </Fragment>
        ))}
      </RadixDropdownMenu.Content>
    </RadixDropdownMenu.Portal>
  );
});

interface ActionMenuProps {
  items: ActionMenuItemData[];
  defaultOpen?: boolean;
  triggerNode?: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  asChild?: boolean;
}

export const ActionMenu = forwardRef<HTMLDivElement, ActionMenuProps>(
  ({ triggerNode, triggerClassName, items, defaultOpen = false, contentClassName = '', asChild = false }, ref) => {
    return (
      <RadixDropdownMenu.Root defaultOpen={defaultOpen}>
        <RadixDropdownMenu.Trigger
          className={
            asChild
              ? undefined
              : classNames(
                  'flex rounded-md items-center justify-center bg-bolt-ds-surfaceOne text-bolt-ds-textPrimary text-sm',
                  triggerClassName,
                  {
                    'px-4 py-2': typeof triggerNode === 'string',
                  },
                )
          }
          asChild={asChild}
        >
          {triggerNode}
        </RadixDropdownMenu.Trigger>

        <ActionMenuPopup items={items} ref={ref} contentClassName={contentClassName} />
      </RadixDropdownMenu.Root>
    );
  },
);
